/* eslint-disable no-unused-vars, max-lines */
import React, {
    useMemo
} from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Button
} from 'shared/components/ui/button';
import {
    Label
} from 'shared/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Plus, Trash2
} from 'lucide-react';
import {
    OelEditor, normalizeOelContext
} from '../../primitives/oel';
import {
    useOpcodeEditorModeOptional
} from '../OpcodeEditorModeContext';
import FieldHelp from '../components/FieldHelp';

/** Procedure step ids for fallback picker — from OEL context. */
export function useProcedureStepIds(oelContext) {
    return useMemo(() => {
        const ctx = normalizeOelContext(oelContext);
        return ctx.allStepIds || ctx.previousStepIds || [];
    }, [oelContext]);
}

/** OEL field in step editors — inline typing + builder in Simple mode. */
export function StepOelEditor(props) {
    const modeCtx = useOpcodeEditorModeOptional();
    const isSimple = modeCtx?.isSimple;
    return (
        <OelEditor
            {...props}
            inline={isSimple}
            compact={isSimple || props.compact}
        />
    );
}

/**
 * KeyValueOelEditor — repeating rows of {key: string, value: OEL string}.
 * Used by API inputs/headers, Tool inputs, Call inputs, Subflow `with`.
 *
 * `value` is an object {key: oelStr}. Empty rows are filtered on emit.
 *
 * `oelContext` (optional) is forwarded to each value cell's OelEditor so the
 * picker can offer @input.x / @<prev_step>.output / definitions / flows.
 */
export function KeyValueOelEditor({
    value = {},
    onChange,
    keyLabel = 'Key',
    valueLabel = 'Value (OEL)',
    addLabel = 'Add row',
    placeholderKey = 'name',
    placeholderValue = '@input.field',
    helpText = null,
    oelContext = null
}) {
    const ctx = normalizeOelContext(oelContext);

    // Local rows preserve empty-key rows while the user is typing. The parent
    // only sees rows with non-empty trimmed keys (emit-time filter). Without
    // local state, "Add row" would create ['', ''] which the filter strips,
    // so the new row would never appear on screen.
    const [rows, setRows] = React.useState(() => Object.entries(value || {}));

    // Resync when the parent value changes externally (load, undo, etc.) AND
    // diverges from our locally-held rows. We compare by emitted shape to
    // avoid clobbering an in-progress empty-key row on every keystroke.
    React.useEffect(() => {
        const emitted = {};
        for (const [k, v] of rows) {
            const t = (k || '').trim();
            if (t) emitted[t] = v;
        }
        const externalKeys = Object.keys(value || {});
        const emittedKeys = Object.keys(emitted);
        const same = externalKeys.length === emittedKeys.length
            && externalKeys.every((k) => emitted[k] === value[k]);
        if (!same) {
            setRows(Object.entries(value || {}));
        }
    // eslint-disable-next-line
    }, [value]);

    function emit(nextRows) {
        setRows(nextRows);
        const obj = {};
        for (const [k, v] of nextRows) {
            const t = (k || '').trim();
            if (t) obj[t] = v;
        }
        onChange(obj);
    }
    function setKey(idx, k) {
        const next = rows.slice();
        next[idx] = [k, next[idx][1]];
        emit(next);
    }
    function setVal(idx, v) {
        const next = rows.slice();
        next[idx] = [next[idx][0], v];
        emit(next);
    }
    function add() {
        emit([...rows, ['', '']]);
    }
    function remove(idx) {
        emit(rows.filter((_, i) => i !== idx));
    }

    return (
        <div className="space-y-1.5">
            {rows.length === 0 ? (
                <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-[11.5px] text-muted-foreground">
                    No rows yet — click <b>{addLabel}</b> below.
                </div>
            ) : (
                rows.map(([k, v], i) => (
                    <div key={i} className="grid grid-cols-[140px_1fr_auto] items-start gap-2">
                        <Input
                            value={k}
                            onChange={(e) => setKey(i, e.target.value)}
                            placeholder={placeholderKey}
                            className="h-8 font-mono text-[12.5px]"
                        />
                        <StepOelEditor
                            value={v || ''}
                            onChange={(nv) => setVal(i, nv)}
                            ctx={ctx}
                            placeholder={placeholderValue}
                            ariaLabel={`${keyLabel} value`}
                            compact
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(i)}
                            className="h-8 px-2 text-muted-foreground hover:text-destructive"
                            title="Remove row"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                ))
            )}
            <Button type="button" variant="outline" size="sm" onClick={add}>
                <Plus className="mr-1 h-3 w-3" /> {addLabel}
            </Button>
            {helpText && (
                <p className="text-[10.5px] text-muted-foreground">{helpText}</p>
            )}
        </div>
    );
}

/**
 * StringChipsEditor — list-of-strings entry with chip badges.
 * Used by Memory step's user_keys / assistant_keys.
 */
export function StringChipsEditor({
    value = [], onChange, placeholder = 'Add value, then Enter'
}) {
    const [draft, setDraft] = React.useState('');
    function add() {
        const v = draft.trim();
        if (!v) return;
        if (!value.includes(v)) onChange([...value, v]);
        setDraft('');
    }
    function remove(item) {
        onChange(value.filter((x) => x !== item));
    }
    return (
        <div className="space-y-1.5">
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {value.map((v) => (
                        <span
                            key={v}
                            className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[11px] font-mono"
                        >
                            {v}
                            <button
                                type="button"
                                onClick={() => remove(v)}
                                className="text-muted-foreground hover:text-destructive"
                            >
                                <Trash2 className="h-2.5 w-2.5" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    className="h-8 text-[12.5px]"
                />
                <Button type="button" variant="outline" size="sm" onClick={add}>
                    <Plus className="mr-1 h-3 w-3" /> Add
                </Button>
            </div>
        </div>
    );
}

/** Section header used inside each inline step editor body. */
export function FieldSection({
    title, hint, children
}) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b border-dashed pb-0.5">
                <h4 className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{title}</h4>
                {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

/** Server-allowed step role hints (OPCODE-SCHEMA §step-common). */
export const STEP_ROLE_OPTIONS = [
    { value: 'data-gather', label: 'data-gather' },
    { value: 'reason', label: 'reason' },
    { value: 'decide', label: 'decide' },
    { value: 'act', label: 'act' },
    { value: 'notify', label: 'notify' }
];

const STEP_ROLE_SET = new Set(STEP_ROLE_OPTIONS.map((o) => o.value));

/**
 * Inline step common — timeout, on_error, role (Simple mode collapsible).
 */
export function StepCommonInlineFields({
    step, onPatchStep, stepIdOptions = []
}) {
    const patch = (partial) => onPatchStep({
        ...step,
        ...partial
    });
    const onErrorAction = (typeof step.on_error === 'string' ? step.on_error : step.on_error?.action) || 'fail';

    return (
        <details className="rounded-md border bg-muted/20 px-3 py-2.5">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Step common (timeout · on_error · role)
            </summary>
            <div className="mt-2 space-y-3">
                <FieldHelp>
                    Optional execution settings for this step only. Leave timeout blank to use
                    the opcode default. On error controls whether a failure stops the run,
                    skips the step, or jumps to another step.
                </FieldHelp>
                {step.role && !STEP_ROLE_SET.has(step.role) && (
                    <FieldHelp>
                        Role <span className="font-mono">{step.role}</span> is invalid and will block
                        save. Choose one of the fixed values below or None.
                    </FieldHelp>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Labeled
                        label="Timeout (ms)"
                        help="100–600000 ms. Blank = opcode-level default."
                    >
                        <Input
                            type="number"
                            min={100}
                            max={600000}
                            value={step.timeout_ms ?? ''}
                            aria-label="step timeout ms"
                            onChange={(e) => patch({
                                timeout_ms: e.target.value === '' ? undefined : Number(e.target.value)
                            })}
                            placeholder="opcode default"
                            className="h-8 text-[12.5px]"
                        />
                    </Labeled>
                    <Labeled
                        label="On error"
                        help="fail: stop the opcode. continue: skip this step. fallback: jump to another step by ID."
                    >
                        <Select
                            value={onErrorAction}
                            onValueChange={(v) => patch({
                                on_error: v === 'fail' ? undefined : { action: v }
                            })}
                        >
                            <SelectTrigger className="h-8 text-[12.5px]" aria-label="step on error">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fail">fail (stop)</SelectItem>
                                <SelectItem value="continue">continue (skip step)</SelectItem>
                                <SelectItem value="fallback">fallback (jump to step)</SelectItem>
                            </SelectContent>
                        </Select>
                        {step.on_error?.action === 'fallback' && (
                            stepIdOptions.length > 0 ? (
                                <Select
                                    className="mt-1.5"
                                    value={step.on_error?.fallback_step_id || ''}
                                    onValueChange={(v) => patch({
                                        on_error: { action: 'fallback', fallback_step_id: v }
                                    })}
                                >
                                    <SelectTrigger className="h-8 text-[12.5px]" aria-label="fallback step id">
                                        <SelectValue placeholder="Pick step" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {stepIdOptions.map((id) => (
                                            <SelectItem key={id} value={id}>{id}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Input
                                    className="mt-1.5 h-8 text-[12.5px]"
                                    value={step.on_error?.fallback_step_id || ''}
                                    aria-label="fallback step id"
                                    onChange={(e) => patch({
                                        on_error: { action: 'fallback', fallback_step_id: e.target.value || undefined }
                                    })}
                                    placeholder="fallback_step_id"
                                />
                            )
                        )}
                    </Labeled>
                    <Labeled
                        label="Role"
                        help="Observability hint only — must be one of the fixed values below (not a free-text tag)."
                    >
                        <Select
                            value={STEP_ROLE_SET.has(step.role) ? step.role : '__none__'}
                            onValueChange={(v) => patch({
                                role: v === '__none__' ? undefined : v
                            })}
                        >
                            <SelectTrigger className="h-8 text-[12.5px]" aria-label="step role">
                                <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {STEP_ROLE_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </Labeled>
                </div>
            </div>
        </details>
    );
}

/** Tiny labeled field wrapper. */
export function Labeled({
    label, children, help
}) {
    return (
        <div className="space-y-1">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </Label>
            {children}
            {help && <p className="text-[10.5px] text-muted-foreground">{help}</p>}
        </div>
    );
}

/**
 * StepCommonAdvanced — V2 step-common control directives.
 * Renders `when` (OEL guard) · `for` (collection loop) · `repeat_until` (retry).
 * Per OPCODE-SCHEMA.md §step-common + §4.7. Hidden on parallel steps (§6.3).
 *
 * Sits below the existing "Step common (timeout · on_error · role)" section
 * in every inline step editor; uses the same <details> collapsible pattern.
 */
export function StepCommonAdvanced({
    step, onPatchStep, oelContext, hideForParallel = true
}) {
    if (!step) return null;
    if (hideForParallel && step.kind === 'parallel') return null;

    const ctx = normalizeOelContext(oelContext);
    /**
     * `repeat_until.until` is evaluated AFTER each iteration of the step,
     * so it legitimately references the step's own output (via the step's
     * own id — there is no `@self` in OEL). Provide a ctx variant that
     * includes the current step in `previousSteps` so the OEL editor's
     * @-picker offers `@<step_id>.output.…` for this field only.
     * `when` and `for` evaluate BEFORE the step runs, so they keep the
     * plain `ctx` (no self reference allowed).
     */
    const repeatCtx = React.useMemo(() => {
        if (!ctx || !step.id) return ctx;
        const prev = ctx.previousSteps || [];
        if (prev.find((s) => s.id === step.id)) return ctx;
        return {
            ...ctx,
            previousSteps: [
                ...prev,
                {
                    id: step.id,
                    kind: step.kind,
                    output_type: step.output_type,
                    raw: step
                }
            ]
        };
    }, [ctx, step]);
    const hasWhen = typeof step.when === 'string' && step.when.length > 0;
    const hasFor = !!step.for && (step.for.collection || step.for.item);
    const hasRepeat = !!step.repeat_until && (step.repeat_until.until || step.repeat_until.max);
    const setCount = [hasWhen, hasFor, hasRepeat].filter(Boolean).length;

    function patch(partial) {
        onPatchStep({
            ...step,
            ...partial
        });
    }
    function patchFor(partial) {
        const next = step.for ? {
            ...step.for,
            ...partial
        } : {
            ...partial
        };
        patch({
            for: next
        });
    }
    function clearFor() {
        const next = {
            ...step
        };
        delete next.for;
        onPatchStep(next);
    }
    function patchRepeat(partial) {
        const next = step.repeat_until ? {
            ...step.repeat_until,
            ...partial
        } : {
            ...partial
        };
        patch({
            repeat_until: next
        });
    }
    function clearRepeat() {
        const next = {
            ...step
        };
        delete next.repeat_until;
        onPatchStep(next);
    }
    function clearWhen() {
        const next = {
            ...step
        };
        delete next.when;
        onPatchStep(next);
    }

    const ITEM_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    const itemValid = !step.for?.item || ITEM_PATTERN.test(step.for.item);
    const maxNum = step.repeat_until?.max;
    const maxValid = maxNum === undefined || maxNum === '' || (Number.isFinite(Number(maxNum)) && Number(maxNum) >= 1 && Number(maxNum) <= 1000);

    return (
        <details
            data-testid="step-common-advanced"
            className="rounded-md border bg-muted/20 px-3 py-2.5"
            open={setCount > 0}
        >
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Advanced control flow ({setCount} set / 3 available)
            </summary>
            <div className="mt-3 space-y-3">
                <FieldHelp>
                    Optional rules for <strong>this step only</strong>. Leave fields empty to run once,
                    normally. Values are OEL expressions — use the <span className="font-mono">/&gt;</span>
                    button to pick <span className="font-mono">@input…</span> or prior step outputs.
                </FieldHelp>

                <Labeled
                    label="when (guard)"
                    help="Checked before the step runs. If the expression is false (or empty), this step is skipped — later steps still run. Example: only fetch orders when @input.order_id is set."
                >
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <p className="mb-1 text-[10px] font-medium text-muted-foreground">Condition (OEL)</p>
                            <StepOelEditor
                                value={step.when || ''}
                                onChange={(v) => (v ? patch({
                                    when: v
                                }) : clearWhen())}
                                ctx={ctx}
                                placeholder="@input.should_run"
                                ariaLabel="when guard expression"
                                compact
                            />
                        </div>
                        {hasWhen && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearWhen}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                aria-label="Clear when guard"
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </Labeled>

                <Labeled
                    label="for (loop)"
                    help="Run this step once per item in a collection. Collection: OEL to an array (e.g. @input.items). Item name: a short identifier — each element is available as @item_name inside the step (e.g. item → @item)."
                >
                    <div className="grid grid-cols-[1fr_140px_auto] items-start gap-2">
                        <div className="min-w-0">
                            <p className="mb-1 text-[10px] font-medium text-muted-foreground">Collection</p>
                            <StepOelEditor
                                value={step.for?.collection || ''}
                                onChange={(v) => patchFor({
                                    collection: v
                                })}
                                ctx={ctx}
                                placeholder="@input.items"
                                ariaLabel="for collection"
                                compact
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-[10px] font-medium text-muted-foreground">Item name</p>
                            <Input
                                value={step.for?.item || ''}
                                onChange={(e) => patchFor({
                                    item: e.target.value
                                })}
                                placeholder="item"
                                className="h-8 font-mono text-[12.5px]"
                                aria-invalid={!itemValid}
                                aria-label="for item identifier"
                            />
                            {!itemValid && (
                                <p className="mt-1 text-[10px] text-destructive">
                                    Must be a valid identifier
                                </p>
                            )}
                        </div>
                        {hasFor && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearFor}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                aria-label="Clear for loop"
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </Labeled>

                <Labeled
                    label="repeat_until"
                    help="Re-run this step after each execution until the condition is true, or max iterations is reached. Condition is checked after the step finishes — you can reference this step's output (e.g. @step_1.output.done). Max is a safety limit (1–1000)."
                >
                    <div className="grid grid-cols-[1fr_120px_auto] items-start gap-2">
                        <div className="min-w-0">
                            <p className="mb-1 text-[10px] font-medium text-muted-foreground">Until (OEL)</p>
                            <StepOelEditor
                                value={step.repeat_until?.until || ''}
                                onChange={(v) => patchRepeat({
                                    until: v
                                })}
                                ctx={repeatCtx}
                                placeholder={step.id ? `@${step.id}.output.done` : '@<step_id>.output.done'}
                                ariaLabel="repeat_until condition"
                                compact
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-[10px] font-medium text-muted-foreground">Max tries</p>
                            <Input
                                type="number"
                                min={1}
                                max={1000}
                                value={maxNum ?? ''}
                                onChange={(e) => patchRepeat({
                                    max: e.target.value === '' ? undefined : Number(e.target.value)
                                })}
                                placeholder="max"
                                className="h-8 text-[12.5px]"
                                aria-invalid={!maxValid}
                                aria-label="repeat_until max iterations"
                            />
                            {!maxValid && (
                                <p className="mt-1 text-[10px] text-destructive">
                                    1 ≤ max ≤ 1000
                                </p>
                            )}
                        </div>
                        {hasRepeat && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={clearRepeat}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                                aria-label="Clear repeat_until"
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                </Labeled>
            </div>
        </details>
    );
}

/**
 * stripParallelControlFlow — defensive save-path helper.
 * Schema §6.3 forbids `when` / `for` / `repeat_until` on a parallel step itself.
 * Recursively strips them (parallel branches may themselves be parallel).
 */
export function stripParallelControlFlow(step) {
    if (!step || typeof step !== 'object') return step;
    let next = step;
    if (step.kind === 'parallel') {
        next = {
            ...step
        };
        delete next.when;
        delete next.for;
        delete next.repeat_until;
    }
    if (Array.isArray(next.branches)) {
        next = {
            ...next,
            branches: next.branches.map(stripParallelControlFlow)
        };
    }
    return next;
}
